import { Component, OnInit, OnDestroy, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Subscription } from 'rxjs';
import { ILLUSTRATOR_MODULES } from './adobe-illustrator.data';
import { IllustratorModule, ModuleProgress, UserProgress, QuizQuestion } from './adobe-illustrator.model';

type AppView = 'overview' | 'module' | 'quiz';

@Component({
    selector: 'app-adobe-illustrator',
    templateUrl: './adobe-illustrator.component.html',
    styleUrls: ['./adobe-illustrator.component.scss'],
    standalone: false
})
export class AdobeIllustratorComponent implements OnInit, OnDestroy {
  modules = ILLUSTRATOR_MODULES;
  currentView: AppView = 'overview';
  selectedModule: IllustratorModule | null = null;

  userProgress: UserProgress = {};
  userId: string | null = null;

  // Quiz state
  currentQuestionIndex = 0;
  selectedAnswerIndex: number | null = null;
  showExplanation = false;
  quizAnswers: number[] = [];
  quizCompleted = false;
  quizScore = 0;

  // Video modal
  activeVideoId: string | null = null;
  activeVideoTitle = '';

  private authSub?: Subscription;
  private progressSub?: Subscription;

  private injector = inject(EnvironmentInjector);

  constructor(
    private auth: AngularFireAuth,
    private db: AngularFireDatabase
  ) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadProgress();
      } else {
        this.userProgress = {};
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.progressSub?.unsubscribe();
  }

  private loadProgress() {
    if (!this.userId) return;
    this.progressSub?.unsubscribe();
    this.progressSub = this.inCtx(() => this.db
      .object<UserProgress>(`illustrator-progress/${this.userId}`)
      .valueChanges())
      .subscribe(progress => {
        this.userProgress = progress || {};
      });
  }

  get mainModules(): IllustratorModule[] {
    return this.modules.filter(m => !m.isBranchPath);
  }

  get branchModules(): IllustratorModule[] {
    return this.modules.filter(m => m.isBranchPath);
  }

  get overallProgress(): number {
    const main = this.mainModules;
    if (!main.length) return 0;
    let points = 0;
    const maxPoints = main.length * 2;
    main.forEach(m => {
      const p = this.userProgress[m.id];
      if (p?.visited) points++;
      if (p?.quizCompleted) points++;
    });
    return Math.round((points / maxPoints) * 100);
  }

  getModuleProgress(moduleId: string): ModuleProgress | null {
    return this.userProgress[moduleId] || null;
  }

  isModuleUnlocked(module: IllustratorModule): boolean {
    if (!module.unlockAfterModule) return true;
    return !!this.userProgress[module.unlockAfterModule]?.visited;
  }

  getModuleStatus(module: IllustratorModule): 'locked' | 'available' | 'visited' | 'completed' {
    if (!this.isModuleUnlocked(module)) return 'locked';
    const p = this.userProgress[module.id];
    if (!p?.visited) return 'available';
    if (p?.quizCompleted) return 'completed';
    return 'visited';
  }

  openModule(module: IllustratorModule) {
    if (!this.isModuleUnlocked(module)) return;
    this.selectedModule = module;
    this.currentView = 'module';
    this.activeVideoId = null;
    this.resetQuiz();
    this.markVisited(module.id);
    window.scrollTo(0, 0);
  }

  private markVisited(moduleId: string) {
    if (!this.userId) return;
    const existing = this.userProgress[moduleId];
    if (existing?.visited) return;
    this.inCtx(() => this.db.object(`illustrator-progress/${this.userId}/${moduleId}`).update({
      visited: true,
      lastVisited: Date.now()
    }));
  }

  goToOverview() {
    this.currentView = 'overview';
    this.selectedModule = null;
    this.activeVideoId = null;
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  backToModule() {
    this.currentView = 'module';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  startQuiz() {
    if (!this.selectedModule?.quiz?.length) return;
    this.currentView = 'quiz';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  private resetQuiz() {
    this.currentQuestionIndex = 0;
    this.selectedAnswerIndex = null;
    this.showExplanation = false;
    this.quizAnswers = [];
    this.quizCompleted = false;
    this.quizScore = 0;
  }

  selectAnswer(index: number) {
    if (this.showExplanation) return;
    this.selectedAnswerIndex = index;
  }

  submitAnswer() {
    if (this.selectedAnswerIndex === null) return;
    this.quizAnswers.push(this.selectedAnswerIndex);
    this.showExplanation = true;
  }

  nextQuestion() {
    if (!this.selectedModule) return;
    if (this.currentQuestionIndex < this.selectedModule.quiz.length - 1) {
      this.currentQuestionIndex++;
      this.selectedAnswerIndex = null;
      this.showExplanation = false;
    } else {
      this.finishQuiz();
    }
  }

  private finishQuiz() {
    if (!this.selectedModule) return;
    const questions = this.selectedModule.quiz;
    const correct = questions.filter((q, i) => this.quizAnswers[i] === q.correctIndex).length;
    this.quizScore = Math.round((correct / questions.length) * 100);
    this.quizCompleted = true;

    if (this.userId) {
      const existing = this.userProgress[this.selectedModule.id];
      const shouldSave = !existing?.quizCompleted || this.quizScore > (existing?.quizScore ?? 0);
      if (shouldSave) {
        this.inCtx(() => this.db.object(`illustrator-progress/${this.userId}/${this.selectedModule.id}`).update({
          quizCompleted: true,
          quizScore: this.quizScore,
          visited: true,
          lastVisited: Date.now()
        }));
      }
    }
  }

  get currentQuestion(): QuizQuestion | null {
    if (!this.selectedModule) return null;
    return this.selectedModule.quiz[this.currentQuestionIndex] || null;
  }

  get isCurrentAnswerCorrect(): boolean {
    if (this.selectedAnswerIndex === null || !this.currentQuestion) return false;
    return this.selectedAnswerIndex === this.currentQuestion.correctIndex;
  }

  isAnswerCorrectForIndex(questionIndex: number): boolean {
    if (!this.selectedModule) return false;
    return this.quizAnswers[questionIndex] === this.selectedModule.quiz[questionIndex].correctIndex;
  }

  getScoreMessage(score: number): string {
    if (score >= 90) return 'Exceptional. Your vectors are clean, your anchors are true, and your bezier curves bring tears to a grown designer\'s eyes.';
    if (score >= 70) return 'Solid work. You\'re not yet a master, but the Pen tool fears you a little. Review any misses and push forward.';
    return 'The artboard humbles us all. Head back to the lesson, absorb the concepts, and come back stronger. Every designer started here.';
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'score-gold';
    if (score >= 70) return 'score-pass';
    return 'score-retry';
  }

  getLevelLabel(level: string): string {
    const labels: Record<string, string> = {
      beginner: 'Beginner',
      intermediate: 'Intermediate',
      advanced: 'Advanced'
    };
    return labels[level] || level;
  }

  openVideo(youtubeId: string | undefined, title: string, searchQuery: string) {
    if (youtubeId) {
      this.activeVideoId = youtubeId;
      this.activeVideoTitle = title;
    } else {
      const url = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
      window.open(url, '_blank');
    }
  }

  closeVideo() {
    this.activeVideoId = null;
    this.activeVideoTitle = '';
  }

  getYouTubeUrl(searchQuery: string): string {
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
  }
}
